/**
 * Luvè delivery promise (snippets/luve-delivery-promise.liquid, blocks/luve-text.liquid, the promise band, the
 * shipping table, the sticky add-to-cart bar).
 *
 * Turns "in 2-3 weeks" into real dates for the shopper: "At your door between Tue 4 Nov and Tue 11 Nov", and the
 * Christmas line into "Order by Sunday 29 November for Christmas", with a live countdown only in its last days (the
 * last week by default; Theme settings > Luvè delivery and shipping). Worked out in the browser, so a cached page
 * never shows yesterday's dates. Every <luve-delivery-promise> on the page does this for the elements inside it:
 *
 *   [data-luve-window]           "At your door between Sun 1 Nov and Sun 8 Nov"
 *   [data-luve-window="phrase"]  "between Sun 1 Nov and Sun 8 Nov", to sit inside a sentence
 *   [data-luve-window="order-now"] "Order now, at your door between Sun 1 Nov and Sun 8 Nov": the dates for an order
 *                                placed today, as a reason to order now (the product page's promise)
 *   [data-luve-window="weeks"]   left as the server wrote it ("At your door in 2-3 weeks")
 *   [data-luve-cutoff]           "Order by Sunday 29 November for Christmas"
 *   [data-luve-cutoff="short"]   "Order by 29 Nov for Christmas", for small capitals in a single line
 *   [data-luve-sentence]         on a cutoff line: ends it with a full stop, so it can be hidden without leaving one
 *   [data-luve-fallback="window"] on a cutoff line: after the order-by date it shows the delivery dates instead of
 *                                going (the announcement bar, where an empty slide would be worse)
 *   [data-luve-cutoff-item]      the list item or card around a cutoff line, hidden and shown with it
 *   [data-luve-countdown]        a live countdown to the end of the order-by day in Sydney: "6 days 3 hrs left";
 *                                ="brief" for "6d 03h left". It shows only while that end is at most N days away,
 *                                N being data-countdown-days on the element (Theme settings > Luvè delivery and
 *                                shipping > "Show the live countdown in the last ... days": 7 by default, 60 all
 *                                season, 0 never). Five weeks on a countdown tell the shopper there is no hurry. Until
 *                                then the line beside it reads as it would alone ("Order by Sunday 29 November for
 *                                Christmas", "7 days left to order for Christmas"); while the countdown shows, the line
 *                                keeps its date. The moment it comes into its last days the page is dated again, so
 *                                the two always agree. It never resets and disappears once the day is over
 *                                (ACCC, Sep 2026: timers that reset are misleading). data-luve-countdown-sep is put
 *                                in front of it, so a separator only shows with the countdown
 *
 * After the order-by date the store stops promising Christmas: the Christmas line goes, and the real dates above it
 * tell the shopper when the tree arrives. Once even the earliest delivery falls after Christmas Day it says so
 * plainly: "Orders placed now arrive after Christmas".
 *
 * The weekly factory order cut-off (Theme settings > Luvè delivery and shipping; the data-factory-* attributes come
 * from snippets/luve-promise-attributes.liquid). Olivia orders the trees from the factory once or twice a week,
 * against real orders: a tree ordered before her cut-off goes into that factory order, one ordered after it waits
 * for the next. While the cut-off is on, every date on the page counts from the factory order an order placed now
 * goes into, and:
 *
 *   [data-luve-factory]          "Order within 2 days 4 hrs to make Thursday's factory order: at your door 5-12 Nov";
 *                                ="short" for "Order in 2d 04h: at your door 5-12 Nov", in a single line
 *   [data-luve-factory-later]    "After that, 12-19 Nov": the dates for an order placed after the cut-off
 *   [data-luve-factory-item]     the item around them, shown while the cut-off is the nearest deadline
 *   [data-luve-window-item]      the item with the plain dates beside it, hidden meanwhile: the factory line has them
 *   [data-luve-weeks]            "2-3 weeks", rewritten from the factory schedule: the days from the factory order
 *                                to the door, plus the shortest and the longest wait for the next factory order
 *   data-luve-one-line           on the element: one line only (the sticky bar), so while the factory line shows the
 *                                Christmas line steps aside
 *
 * The factory countdown runs to a real cut-off. When it ends, that factory order has closed: the line moves to the
 * next cut-off, whose dates are later. No deadline repeats and none is invented, so each one changes the delivery
 * date. Which line wins is the nearer deadline. Most of the season that is the factory cut-off, and the Christmas line
 * stays beside it without a countdown, so there is never more than one countdown. When the Christmas order-by day
 * ends before the next cut-off, the Christmas line keeps its countdown (in its last days, as above) and the factory
 * line waits. After the order-by date the factory line carries on until even its earliest delivery falls after
 * Christmas Day. If the factory days make the order-by date untrue (an order placed on it could arrive after Christmas
 * Eve), the Christmas line moves to the last cut-off that still arrives in time: "Order by 10am Friday 27 November for
 * Christmas".
 *
 * "Preview theme only" (data-factory="preview") shows all of this on an unpublished theme, in its preview link and the
 * theme editor, and never on the live store, where Shopify.theme.role is "main".
 *
 *   [data-luve-christmas-weeks]  "Order now and it's up by mid-November: about 6 weeks of Christmas.": the latest
 *                                delivery date, and whole weeks from it to Christmas Day. Its own setting: ="on", or
 *                                ="preview" for the preview theme only. Its item, [data-luve-christmas-weeks-item],
 *                                is hidden once less than a week is left
 *
 * The date arithmetic is exported so tools/delivery-promise.test.mjs can check it.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const COUNTDOWN_DAYS = 7;
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const short = new Intl.DateTimeFormat('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
const long = new Intl.DateTimeFormat('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });
const dayMonth = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short' });
const monthName = new Intl.DateTimeFormat('en-AU', { month: 'long' });

/** Joins a date's parts without the comma en-AU puts after the weekday: "Sun 1 Nov" */
const withoutCommas = (format, date) =>
  format
    .formatToParts(date)
    .filter((part) => part.type !== 'literal')
    .map((part) => part.value)
    .join(' ');

/** @param {Date} date */
const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const sydneyParts = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'Australia/Sydney',
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/**
 * The date and the time on a Sydney clock at an instant
 *
 * @param {Date} instant
 */
function sydneyClock(instant) {
  const parts = Object.fromEntries(sydneyParts.formatToParts(instant).map((part) => [part.type, part.value]));
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    // Some engines write midnight as 24 even with h23
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

/** How far a Sydney clock is ahead of UTC at an instant, in milliseconds (11 hours in summer) */
function sydneyOffset(ms) {
  const clock = sydneyClock(new Date(ms));
  const asUtc = Date.UTC(clock.year, clock.month - 1, clock.day, clock.hour, clock.minute, clock.second);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

/**
 * The instant a time on a Sydney clock comes round, whatever the shopper's own time zone. Hour 24 is midnight at the
 * end of the day.
 */
export function sydneyTime(year, month, day, hour = 0, minute = 0, second = 0, ms = 0) {
  const wall = Date.UTC(year, month - 1, day, hour, minute, second, ms);
  const guess = wall - sydneyOffset(wall);
  return new Date(wall - sydneyOffset(guess));
}

/**
 * The last moment of a calendar day in Sydney, whatever the shopper's own time zone: the order-by day ends at
 * midnight where the orders are placed.
 *
 * @param {Date} day - Its year, month and date are used
 */
export const sydneyEndOfDay = (day) =>
  sydneyTime(day.getFullYear(), day.getMonth() + 1, day.getDate(), 23, 59, 59, 999);

/** Today's date in Sydney, as a calendar date */
const sydneyToday = (ms) => {
  const clock = sydneyClock(new Date(ms));
  return new Date(clock.year, clock.month - 1, clock.day);
};

/** Calendar days, not milliseconds, so a daylight saving change cannot shift the date. */
const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/** Whole calendar days from one date to another */
const daysBetween = (from, to) => Math.round((to.getTime() - from.getTime()) / DAY_MS);

const pad = (n) => String(n).padStart(2, '0');

/**
 * How long is left, in the countdown's words: "2 days 4 hrs", "5h 20m", "12m 05s"; brief: "2d 04h"
 *
 * @param {number} ms
 * @param {boolean} brief
 */
export function durationText(ms, brief) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (days >= 1) {
    return brief
      ? `${days}d ${pad(hours)}h`
      : `${days} ${days === 1 ? 'day' : 'days'} ${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
  }
  if (hours >= 1) return `${hours}h ${pad(minutes)}m`;
  return `${minutes}m ${pad(seconds)}s`;
}

/**
 * @param {number} ms - Time left
 * @param {boolean} brief
 */
const countdownText = (ms, brief) => `${durationText(ms, brief)} left`;

/**
 * Whether the live countdown to the end of the order-by day shows: only in its last days, and never once it is over.
 * Five weeks on a countdown tell the shopper there is no hurry; the order-by date says it better until the end is near.
 *
 * @param {number} left - Milliseconds to the end of the order-by day in Sydney
 * @param {number} days - Show it in the last this many days: 7 for the last week, 0 for never
 */
export const countdownShown = (left, days) => left > 0 && left <= days * DAY_MS;

/**
 * How many days before the end of the order-by day the countdown shows, from data-countdown-days (Theme settings >
 * Luvè delivery and shipping). Without a whole number there, the last week, the setting's default.
 *
 * @param {string | undefined} value
 */
export function readCountdownDays(value) {
  const text = String(value ?? '').trim();
  return /^\d{1,3}$/.test(text) ? Number(text) : COUNTDOWN_DAYS;
}

/**
 * A [data-luve-window] line for an order placed now, or null for ="weeks", which keeps the server's words
 *
 * @param {string | null} format - The attribute's value: empty, "phrase", "order-now" or "weeks"
 * @param {Date} earliest
 * @param {Date} latest
 */
export function windowText(format, earliest, latest) {
  if (format === 'weeks') return null;
  const dates = `between ${withoutCommas(short, earliest)} and ${withoutCommas(short, latest)}`;
  if (format === 'phrase') return dates;
  return format === 'order-now' ? `Order now, at your door ${dates}` : `At your door ${dates}`;
}

/** @param {string | undefined} value - YYYY-MM-DD */
function parseDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? '').trim());
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** "10am", "5:30pm", "noon" */
function clockText(hour, minute) {
  if (hour === 12 && minute === 0) return 'noon';
  const suffix = hour < 12 ? 'am' : 'pm';
  const twelve = hour % 12 || 12;
  return minute ? `${twelve}:${pad(minute)}${suffix}` : `${twelve}${suffix}`;
}

/**
 * Two delivery dates as one short range: "5-12 Nov", "28 Nov-5 Dec"
 *
 * @param {Date} from
 * @param {Date} to
 */
export function rangeText(from, to) {
  if (from.getTime() === to.getTime()) return withoutCommas(dayMonth, to);
  if (from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth()) {
    return `${from.getDate()}-${withoutCommas(dayMonth, to)}`;
  }
  return `${withoutCommas(dayMonth, from)}-${withoutCommas(dayMonth, to)}`;
}

/**
 * @typedef {object} Schedule - The weekly factory order cut-off, from the theme settings
 * @property {number[]} days - The factory order weekdays, 0 for Sunday
 * @property {number} hour - The cut-off on a Sydney clock; 24 is midnight at the end of the day
 * @property {number} minute
 * @property {boolean} before - The cut-off falls the day before the factory order
 * @property {number} min - Earliest delivery, days after the factory order
 * @property {number} max - Latest delivery, days after the factory order
 */

/**
 * The factory schedule from a promise element's data attributes, or null while the cut-off is off, is set to the
 * preview theme only and this is not one, or is not set up. A schedule it cannot read shows nothing.
 *
 * @param {Record<string, string | undefined>} data - The element's dataset
 * @param {boolean} previewTheme - This is an unpublished theme or the theme editor
 * @returns {Schedule | null}
 */
export function readSchedule(data, previewTheme) {
  const mode = data.factory;
  if (mode !== 'on' && !(mode === 'preview' && previewTheme)) return null;
  const days = [
    ...new Set(
      String(data.factoryDays ?? '')
        .split(',')
        .map((day) => day.trim())
        .filter((day) => /^[0-6]$/.test(day))
        .map(Number)
    ),
  ].sort((a, b) => a - b);
  const time = /^(\d{1,2}):(\d{2})$/.exec(String(data.factoryTime ?? '').trim());
  /** @param {string | undefined} value */
  const wholeDays = (value) => (/^\d{1,3}$/.test(String(value ?? '').trim()) ? Number(value) : NaN);
  const min = wholeDays(data.factoryMin);
  const max = wholeDays(data.factoryMax);
  if (!days.length || !time || Number.isNaN(min) || Number.isNaN(max)) return null;
  const hour = Number(time[1]);
  const minute = Number(time[2]);
  if (minute > 59 || hour > 24 || (hour === 24 && minute > 0)) return null;
  return { days, hour, minute, before: data.factoryCutoffDay === 'before', min, max: Math.max(min, max) };
}

/**
 * The next factory order cut-offs after an instant, earliest first: when each one closes, and the date of the factory
 * order it is for.
 *
 * @param {Schedule} schedule
 * @param {number} after - Milliseconds
 * @param {number} [count]
 * @returns {{ closes: Date, factory: Date }[]}
 */
export function nextCutoffs(schedule, after, count = 2) {
  const today = sydneyToday(after);
  const found = [];
  for (let ahead = 0; found.length < count && ahead <= 7 * (count + 1); ahead += 1) {
    const factory = addDays(today, ahead);
    if (!schedule.days.includes(factory.getDay())) continue;
    const day = schedule.before ? addDays(factory, -1) : factory;
    const closes = sydneyTime(day.getFullYear(), day.getMonth() + 1, day.getDate(), schedule.hour, schedule.minute);
    if (closes.getTime() > after) found.push({ closes, factory });
  }
  return found;
}

/**
 * The delivery window in days from the shopper's order: the days from the factory order to the door, plus the
 * shortest and the longest wait for the next factory order. snippets/luve-lead-window.liquid works out the same, so
 * the server's "2-3 weeks" matches.
 *
 * @param {Schedule} schedule
 */
export function leadWindow(schedule) {
  const { days } = schedule;
  // The longest stretch from one factory order to the next: a week with one order day
  const longestGap = Math.max(...days.map((day, i) => (days[(i + 1) % days.length] - day + 7) % 7 || 7));
  const atMidnight = schedule.hour === 24 || (schedule.hour === 0 && schedule.minute === 0);
  // The calendar day the cut-off falls on, from the factory order day: -1 the day before, +1 for midnight at its end
  const shift = (schedule.before ? -1 : 0) + (schedule.hour === 24 ? 1 : 0);
  return {
    min: schedule.min - shift + (atMidnight ? 1 : 0),
    max: schedule.max + longestGap - shift,
  };
}

/**
 * When ordering for Christmas ends. The order-by day ends at midnight in Sydney. With the factory schedule on, it ends
 * sooner if an order placed on that day would go into a factory order that could arrive after Christmas Eve: then at
 * the last cut-off whose factory order still arrives in time.
 *
 * @param {Date} cutoff - The order-by date
 * @param {Schedule | null} schedule
 * @returns {{ end: Date, day: Date, hour: number | null, minute: number }} When it ends, and how to name it: the day,
 *   and the time of day when it is not midnight. Null when no factory order arrives in time
 */
export function christmasDeadline(cutoff, schedule) {
  const end = sydneyEndOfDay(cutoff);
  if (!schedule) return { end, day: cutoff, hour: null, minute: 0 };
  const eve = new Date(cutoff.getFullYear(), 11, 24);
  /** @param {{ factory: Date }} cut */
  const inTime = (cut) => addDays(cut.factory, schedule.max) <= eve;
  const [after] = nextCutoffs(schedule, end.getTime(), 1);
  if (after && inTime(after)) return { end, day: cutoff, hour: null, minute: 0 };

  // The last cut-off before the order-by day ends whose factory order still arrives by Christmas Eve
  let last = null;
  for (let at = end.getTime() - 70 * DAY_MS; ; ) {
    const [cut] = nextCutoffs(schedule, at, 1);
    if (!cut || cut.closes.getTime() > end.getTime()) break;
    if (inTime(cut)) last = cut;
    at = cut.closes.getTime();
  }
  if (!last) return null;
  const day = schedule.before ? addDays(last.factory, -1) : last.factory;
  // Midnight at the end of the day is just "by" that day
  const hour = schedule.hour === 24 ? null : schedule.hour;
  return { end: last.closes, day, hour, minute: schedule.minute };
}

/**
 * @param {number} daysLeft - Days from today to the order-by date
 * @param {{ day: Date, hour: number | null, minute: number } | null} deadline - Null once no order arrives in time
 * @param {boolean} brief - The short form for single-line labels
 * @param {boolean} passed - Ordering for Christmas has ended
 */
function christmasLine(daysLeft, deadline, brief, passed) {
  if (passed || !deadline) return brief ? 'Arrives after Christmas' : 'Orders placed now arrive after Christmas';
  if (daysLeft <= 0) return 'Last day to order for Christmas';
  if (daysLeft <= COUNTDOWN_DAYS) return `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left to order for Christmas`;
  const time = deadline.hour === null ? '' : `${clockText(deadline.hour, deadline.minute)} `;
  return brief
    ? `Order by ${time}${withoutCommas(dayMonth, deadline.day)} for Christmas`
    : `Order by ${time}${withoutCommas(long, deadline.day)} for Christmas`;
}

/**
 * How to name a factory order: "today's", "tomorrow's", "Thursday's", "next Thursday's" (a week from today)
 *
 * @param {Date} factory
 * @param {Date} today - Today in Sydney
 */
export function factoryName(factory, today) {
  const ahead = daysBetween(today, factory);
  if (ahead <= 0) return "today's";
  if (ahead === 1) return "tomorrow's";
  const name = WEEKDAYS[factory.getDay()];
  return ahead >= 7 ? `next ${name}'s` : `${name}'s`;
}

/**
 * "Order now and it's up by mid-November: about 6 weeks of Christmas.", or null with less than a week to go
 *
 * @param {Date} latest - The latest delivery date for an order placed now
 * @param {Date} christmasDay
 */
export function christmasWeeksLine(latest, christmasDay) {
  const weeks = Math.floor(daysBetween(latest, christmasDay) / 7);
  if (weeks < 1) return null;
  const date = latest.getDate();
  const month = monthName.format(latest);
  const when = date <= 10 ? `early ${month}` : date <= 20 ? `mid-${month}` : `late ${month}`;
  return `Order now and it's up by ${when}: about ${weeks === 1 ? 'a week' : `${weeks} weeks`} of Christmas.`;
}

/**
 * Everything the promise says at an instant, for an order placed now
 *
 * @param {object} input
 * @param {number} input.now - Milliseconds
 * @param {Date} input.today - Today on the shopper's own calendar
 * @param {number} input.min - Earliest delivery, days after ordering
 * @param {number} input.max - Latest delivery, days after ordering
 * @param {Date | null} input.cutoff - The Christmas order-by date
 * @param {Schedule | null} input.schedule - The factory schedule while it is on
 */
export function plan({ now, today, min, max, cutoff, schedule }) {
  const [next, later] = schedule ? nextCutoffs(schedule, now, 2) : [];
  const factory = Boolean(schedule && next);
  const earliest = factory ? addDays(next.factory, schedule.min) : addDays(today, min);
  const latest = factory ? addDays(next.factory, schedule.max) : addDays(today, max);
  const christmasDay = new Date((cutoff ?? today).getFullYear(), 11, 25);
  const missesChristmas = earliest > christmasDay;
  const deadline = cutoff ? christmasDeadline(cutoff, factory ? schedule : null) : null;
  const christmasOpen = Boolean(deadline && deadline.end.getTime() > now);
  // The factory line: until even its earliest delivery is after Christmas, and while it comes before the order-by end
  const factoryLive = factory && !missesChristmas;
  const factoryFirst = factoryLive && (!christmasOpen || next.closes.getTime() <= deadline.end.getTime());
  const lead = factory ? leadWindow(schedule) : null;
  // The page is planned again when a cut-off passes (the dates move on) or ordering for Christmas ends
  const changes = [factory ? next.closes.getTime() : Infinity, christmasOpen ? deadline.end.getTime() : Infinity];
  return {
    next: factory ? next : null,
    later: factory ? later ?? null : null,
    earliest,
    latest,
    laterEarliest: factory && later ? addDays(later.factory, schedule.min) : null,
    laterLatest: factory && later ? addDays(later.factory, schedule.max) : null,
    weeks: lead ? { min: Math.floor(lead.min / 7), max: Math.ceil(lead.max / 7) } : null,
    christmasDay,
    missesChristmas,
    deadline,
    christmasOpen,
    factoryFirst,
    replanAt: Math.min(...changes),
  };
}

/** An unpublished theme (its preview link) or the theme editor, where "Preview theme only" shows */
function onPreviewTheme() {
  const shopify = /** @type {any} */ (window).Shopify;
  if (shopify?.designMode) return true;
  const role = shopify?.theme?.role;
  return typeof role === 'string' && role !== 'main';
}

class LuveDeliveryPromise extends HTMLElement {
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #ticker;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  /** @type {Date | null} */
  #end = null;
  /** @type {ReturnType<typeof plan> | null} */
  #plan = null;
  /** The factory line is showing, so the Christmas countdown is not */
  #factoryShown = false;
  /** Days before the end of the order-by day the Christmas countdown shows (data-countdown-days) */
  #countdownDays = COUNTDOWN_DAYS;
  /**
   * The Christmas countdown is in its last days and no factory line is showing: it shows, and the line beside it keeps
   * its date
   */
  #countdownShown = false;

  connectedCallback() {
    this.update();
  }

  disconnectedCallback() {
    clearInterval(this.#ticker);
    clearTimeout(this.#timer);
  }

  /**
   * The live countdowns: the factory cut-off's while its line shows, otherwise the order-by day's in its last days,
   * hidden before them and once over. When the order-by countdown comes into its last days the page is dated again,
   * so the Christmas line beside it keeps its date from then on.
   *
   * @param {number} [now] - update() passes its own moment, so the first tick agrees with what it just wrote
   */
  #tick = (now = Date.now()) => {
    if (this.#plan && now >= this.#plan.replanAt) {
      this.update();
      return;
    }
    const left = this.#end && !this.#factoryShown ? this.#end.getTime() - now : -1;
    const shown = countdownShown(left, this.#countdownDays);
    if (shown !== this.#countdownShown) {
      this.update();
      return;
    }
    for (const element of this.querySelectorAll('[data-luve-countdown]')) {
      const node = /** @type {HTMLElement} */ (element);
      node.hidden = !shown;
      if (shown) {
        const text = countdownText(left, node.getAttribute('data-luve-countdown') === 'brief');
        node.textContent = `${node.getAttribute('data-luve-countdown-sep') ?? ''}${text}`;
      }
    }
    const next = this.#factoryShown ? this.#plan?.next : null;
    if (next) {
      for (const node of this.querySelectorAll('[data-luve-factory-left]')) {
        node.textContent = durationText(next.closes.getTime() - now, node.getAttribute('data-luve-factory-left') === 'brief');
      }
    }
    if (left <= 0 && !next) clearInterval(this.#ticker);
  };

  /** Plans the page again when the next cut-off passes or ordering for Christmas ends, even with no countdown */
  #wait() {
    clearTimeout(this.#timer);
    const at = this.#plan?.replanAt ?? Infinity;
    if (!Number.isFinite(at)) return;
    // Timers cap out at about 24 days, so a far-off change is checked again daily
    this.#timer = setTimeout(() => (Date.now() >= at ? this.update() : this.#wait()), Math.min(Math.max(at - Date.now(), 0) + 50, DAY_MS));
  }

  /**
   * @param {Element} line - A [data-luve-factory] line
   * @param {ReturnType<typeof plan>} p
   * @param {Date} today - Today in Sydney
   */
  #writeFactory(line, p, today) {
    if (!p.next) return;
    const brief = line.getAttribute('data-luve-factory') === 'short';
    const left = document.createElement('span');
    left.className = 'luve-countdown';
    left.setAttribute('data-luve-factory-left', brief ? 'brief' : '');
    const dates = document.createElement('span');
    dates.className = 'luve-nowrap';
    dates.textContent = rangeText(p.earliest, p.latest);
    if (brief) {
      line.replaceChildren('Order in ', left, ': at your door ', dates);
    } else {
      line.replaceChildren(
        'Order within ',
        left,
        ` to make ${factoryName(p.next.factory, today)} factory order: at your door `,
        dates
      );
    }
  }

  update() {
    const now = Date.now();
    const today = startOfDay(new Date(now));
    const min = Number(this.dataset.min) || 14;
    const max = Math.max(min, Number(this.dataset.max) || 21);
    const cutoff = parseDate(this.dataset.cutoff);
    const previewTheme = onPreviewTheme();
    const schedule = readSchedule(this.dataset, previewTheme);
    const p = plan({ now, today, min, max, cutoff, schedule });
    this.#plan = p;

    for (const line of this.querySelectorAll('[data-luve-window]')) {
      const text = windowText(line.getAttribute('data-luve-window'), p.earliest, p.latest);
      if (text !== null) line.textContent = text;
    }

    // "2-3 weeks" from the factory schedule: the server writes the same once the cut-off is On
    if (p.weeks) {
      for (const span of this.querySelectorAll('[data-luve-weeks]')) {
        span.textContent = `${p.weeks.min}-${p.weeks.max} weeks`;
      }
    }

    // The factory line, where this element has one, while its cut-off is the nearest deadline
    const factoryShown = p.factoryFirst && this.querySelector('[data-luve-factory]') !== null;
    this.#factoryShown = factoryShown;
    this.toggleAttribute('data-factory-shown', factoryShown);
    for (const item of this.querySelectorAll('[data-luve-factory-item]')) {
      /** @type {HTMLElement} */ (item).hidden = !factoryShown;
    }
    for (const item of this.querySelectorAll('[data-luve-window-item]')) {
      /** @type {HTMLElement} */ (item).hidden = factoryShown;
    }
    if (factoryShown) {
      const sydney = sydneyToday(now);
      for (const line of this.querySelectorAll('[data-luve-factory]')) this.#writeFactory(line, p, sydney);
      for (const line of this.querySelectorAll('[data-luve-factory-later]')) {
        line.textContent = p.laterEarliest && p.laterLatest ? `After that, ${rangeText(p.laterEarliest, p.laterLatest)}` : '';
      }
    }

    // Weeks of Christmas, on its own setting: "on", or "preview" for the preview theme only
    const weeksLine = christmasWeeksLine(p.latest, p.christmasDay);
    for (const line of this.querySelectorAll('[data-luve-christmas-weeks]')) {
      const mode = line.getAttribute('data-luve-christmas-weeks');
      const shown = Boolean(weeksLine) && (mode === 'on' || (mode === 'preview' && previewTheme));
      line.textContent = shown ? weeksLine : '';
      /** @type {HTMLElement} */ (line.closest('[data-luve-christmas-weeks-item]') || line).hidden = !shown;
    }

    const cutoffLines = this.querySelectorAll('[data-luve-cutoff]');

    clearInterval(this.#ticker);
    this.#end = p.deadline ? p.deadline.end : null;
    // The order-by countdown shows only in its last days; #tick dates the page again when it comes into them
    this.#countdownDays = readCountdownDays(this.dataset.countdownDays);
    const left = this.#end && !factoryShown ? this.#end.getTime() - now : -1;
    this.#countdownShown = countdownShown(left, this.#countdownDays);
    const christmasTicking = Boolean(this.querySelector('[data-luve-countdown]'));
    if (christmasTicking || factoryShown) {
      this.#tick(now);
      if (factoryShown || (this.#end && this.#end.getTime() > Date.now())) this.#ticker = setInterval(this.#tick, 1000);
    }
    this.#wait();

    if (!cutoff || !cutoffLines.length) return;

    const deadline = p.deadline;
    const daysLeft = deadline ? daysBetween(today, deadline.day) : -1;
    const passed = !deadline || !p.christmasOpen || daysLeft < 0;
    // Between the order-by date and the day the earliest delivery misses Christmas, say nothing about Christmas
    const silent = passed && !p.missesChristmas;
    this.toggleAttribute('data-after-cutoff', passed);
    this.toggleAttribute('data-countdown', !passed && daysLeft <= COUNTDOWN_DAYS);

    for (const line of cutoffLines) {
      const container = /** @type {HTMLElement} */ (line.closest('[data-luve-cutoff-item]') || line);
      // A single line carries one message: while the factory line shows, the Christmas line steps aside
      if (factoryShown && this.hasAttribute('data-luve-one-line')) {
        container.hidden = true;
        continue;
      }
      if (silent && line.getAttribute('data-luve-fallback') === 'window') {
        container.hidden = false;
        line.textContent = windowText(null, p.earliest, p.latest);
        continue;
      }
      container.hidden = silent;
      if (silent) continue;
      const brief = line.getAttribute('data-luve-cutoff') === 'short';
      // A line with a live countdown showing beside it keeps the date: the countdown already says how long is left.
      // Before the countdown's last days (it is hidden) the line reads as it would alone
      const beside = Boolean(line.parentElement?.querySelector('[data-luve-countdown]'));
      const timed = this.#countdownShown && beside && daysLeft > 0;
      const text = christmasLine(timed ? COUNTDOWN_DAYS + 1 : daysLeft, deadline, brief, passed);
      line.textContent = line.hasAttribute('data-luve-sentence') ? `${text}.` : text;
    }
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('luve-delivery-promise')) {
  customElements.define('luve-delivery-promise', LuveDeliveryPromise);
}
